import React, { useState, useEffect, useRef } from "react";
import { useTheme } from "../context/ThemeContext";
import {
  ColorblindImage,
  ColorblindImageContainer,
} from "../components/ColorblindImage";
import { DEFAULT_CARD_BACKGROUND } from "../constants/colorSchemes";
import { contrastingTextColor } from "../utils/colorUtils";
import {
  generateFullPeriodicTable,
  generateNursingTerms,
  generateScienceTerms,
  generateCountingSvg,
} from "../utils/RandomGenerators";
import { determineCollectionType, getAllowedItemTypes, getItemTypeDisplayName } from "../utils/collectionTypeUtils";
import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import SortableGridItem, {
  gridCollisionDetection,
} from "./SortableGridItem";
import { PICTURE_QUIZ_CATEGORY } from "../utils/pictureQuizUtils";
import { PICTURE_WORDS_CATEGORY } from "../utils/pictureWordsUtils";
import PairSlideImages from "./PairSlideImages";
import { canPairUploadedImage, slideImageFields } from "../utils/slideImages";
import {
  COLLECTION_STAGES,
  COLLECTION_STAGE_LABELS,
  CollectionStage,
  normalizeCollectionStage,
} from "../constants/collectionStages";

interface EditedItem {
  name: string;
  id?: number;
  svg?: string;
  svgs?: string[];
  count?: number;
  answer?: string;
  answerError?: boolean;
  clientId: string;
}

const createClientId = () =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `item-${Date.now()}-${Math.random().toString(36).slice(2)}`;

const isImageSrc = (value?: string) =>
  !!value &&
  (value.startsWith("data:image") ||
    value.startsWith("data:application") ||
    value.startsWith("blob:"));

const isCountingSvg = (value?: string) =>
  !!value &&
  (value.startsWith("<svg") || value.startsWith("data:image/svg+xml"));

const getItemPreviewSrc = (item: EditedItem): string | null => {
  if (isImageSrc(item.svg)) return item.svg as string;
  if (isImageSrc(item.name)) return item.name;
  return null;
};

/**
 * Props for the EditCollectionModal component
 */
interface EditCollectionModalProps {
  /** Controls whether the modal is visible */
  isOpen: boolean;
  /** Callback function to close the modal */
  onClose: () => void;
  /** Name of the collection being edited */
  collectionName: string;
  /** Array of items in the collection */
  items: {
    name: string;
    id?: number;
    svg?: string;
    svgs?: string[];
    count?: number;
    answer?: string;
  }[];
  /** Callback function to save changes to the collection */
  onSave: (
    items: {
      name: string;
      id?: number;
      svg?: string;
      svgs?: string[];
      count?: number;
      answer?: string;
    }[],
    collectionName: string,
    isPublic: boolean,
    stage: CollectionStage,
  ) => Promise<void>;
  /** Type of collection (e.g., "custom", "scienceTerms", etc.) */
  type: string;
  /** Category of the collection */
  category: string;
  /** Optional mastery stage */
  stage?: string;
  /** Boolean indicating if the collection is public */
  isPublic: boolean;
}

/**
 * Modal component for editing collection details and items.
 * Supports drag-and-drop reordering, various item types (custom, science terms, etc.),
 * and different visualization options based on collection type.
 *
 * @param props - The component props
 * @returns A modal for editing collection contents and settings
 */
const EditCollectionModal: React.FC<EditCollectionModalProps> = ({
  isOpen,
  onClose,
  collectionName,
  items,
  onSave,
  type,
  category,
  stage = "none",
  isPublic,
}) => {
  const { theme, adjustColorForColorblindness } = useTheme();
  console.log("Collection type:", type); // Use it in your logic
  
  // Determine the actual collection type based on items and category
  const actualCollectionType = determineCollectionType(category, items);
  const allowedItemTypes = getAllowedItemTypes(actualCollectionType);
  
  console.log("Actual collection type:", actualCollectionType);
  console.log("Allowed item types:", allowedItemTypes);
  
  const [editedItems, setEditedItems] = useState<EditedItem[]>([]);
  const [editedCollectionName, setEditedCollectionName] =
    useState(collectionName);
  const [isCollectionPublic, setIsCollectionPublic] = useState(isPublic); // State for checkbox
  const [editedStage, setEditedStage] = useState<CollectionStage>(
    normalizeCollectionStage(stage),
  );
  const modalRef = useRef<HTMLDivElement>(null);
  const [selectedItemType, setSelectedItemType] = useState<string>(allowedItemTypes[0] || "custom"); // Default to first allowed type
  const [availableTerms, setAvailableTerms] = useState<string[]>([]); // State for available terms
  const [selectedTerm, setSelectedTerm] = useState<string>(""); // State for the selected term
  const [customItemName, setCustomItemName] = useState<string>(""); // State for custom item name
  const [pictureQuizSaveAttempted, setPictureQuizSaveAttempted] =
    useState(false);
  const [pictureWordsSaveAttempted, setPictureWordsSaveAttempted] =
    useState(false);
  const [dotColor, setDotColor] = useState<string>("blue");
  const [dotShape, setDotShape] = useState<string>("circle");
  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 200, tolerance: 8 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const predefinedItems = {
    scienceTerms: generateScienceTerms(20),
    periodicTable: generateFullPeriodicTable(),
    nursingTerms: generateNursingTerms(20),
    numberSense: [],
  };

  const decodeSvgData = (data: string) => {
    try {
      if (data.startsWith("data:image/svg+xml;base64,")) {
        return decodeURIComponent(atob(data.split(",")[1]));
      } else if (data.startsWith("data:image/svg+xml,")) {
        return decodeURIComponent(data.split(",")[1]);
      } else if (data.startsWith("<svg")) {
        return data;
      } else if (data.startsWith("data:image/")) {
        // Return the original data URL for non-SVG images
        return data;
      }
      return null;
    } catch (e) {
      console.error("Error decoding data:", e);
      return null;
    }
  };

  useEffect(() => {
    setEditedCollectionName(collectionName);
    setIsCollectionPublic(isPublic);
    setEditedStage(normalizeCollectionStage(stage));
  }, [collectionName, isPublic, stage]);

  useEffect(() => {
    const processedItems = items.map((item, index) => {
      const clientId =
        item.id != null ? `saved-${item.id}` : `loaded-${index}-${item.name}`;
      // For items with SVG data
      if (item.svg) {
        return {
          name: item.name,
          id: item.id,
          svg: item.svg,
          svgs: item.svgs,
          count: item.count || 1,
          answer: item.answer,
          clientId,
        };
      }
      // For all other items
      return {
        name: typeof item.name === "object" ? JSON.stringify(item.name) : String(item.name),
        id: item.id,
        clientId,
      };
    });
    setEditedItems(processedItems);
  }, [items]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    // Update available terms based on selected item type
    if (selectedItemType === "scienceTerms") {
      setAvailableTerms(generateScienceTerms(20));
    } else if (selectedItemType === "nursingTerms") {
      setAvailableTerms(generateNursingTerms(20));
    } else if (selectedItemType === "periodicTable") {
      setAvailableTerms(predefinedItems.periodicTable);
    } else {
      setAvailableTerms([]);
    }
    setSelectedTerm("");
  }, [selectedItemType]);

  const handleAddItem = () => {
    if (selectedItemType === "custom") {
      setEditedItems([
        {
          name: customItemName || "New Custom Item",
          clientId: createClientId(),
        },
        ...editedItems,
      ]);
      setCustomItemName("");
    } else if (selectedItemType === "numberSense") {
      const newCount = 1;
      const newSvg = generateCountingSvg(newCount, dotColor, dotShape);
      const newItem = {
        name: `Number Sense Image (Count: ${newCount})`,
        svg: newSvg,
        count: newCount,
        clientId: createClientId(),
      };
      setEditedItems([newItem, ...editedItems]);
    } else {
      setEditedItems([
        { name: selectedTerm, clientId: createClientId() },
        ...editedItems,
      ]);
      setSelectedTerm("");
    }
  };

  const handleRemoveItem = (index: number) => {
    const newItems = editedItems.filter((_, i) => i !== index);
    setEditedItems(newItems);
  };

  const handleEditItemName = (index: number, name: string) => {
    setEditedItems((currentItems) =>
      currentItems.map((item, itemIndex) =>
        itemIndex === index ? { ...item, name } : item,
      ),
    );
  };

  const handleSave = () => {
    if (category === PICTURE_QUIZ_CATEGORY) {
      const missingAnswer = editedItems.some(
        (item) => item.svg && !item.answer?.trim(),
      );
      if (missingAnswer) {
        setPictureQuizSaveAttempted(true);
        setEditedItems((currentItems) =>
          currentItems.map((item) => ({
            ...item,
            answerError: Boolean(item.svg && !item.answer?.trim()),
          })),
        );
        alert("Please enter an answer for each picture.");
        return;
      }
    }

    if (category === PICTURE_WORDS_CATEGORY) {
      const missingWord = editedItems.some(
        (item) => item.svg && !item.name?.trim(),
      );
      if (missingWord) {
        setPictureWordsSaveAttempted(true);
        alert("Please enter a word for each picture.");
        return;
      }
    }

    const processedItems = editedItems.map((item) => {
      // Check for svg property instead of relying on type
      if (item.svg) {
        return {
          name: item.name,
          ...slideImageFields(item.svgs?.[0] || item.svg, item.svgs?.[1]),
          count: item.count || 1,
          answer: item.answer?.trim(),
          id: item.id,
        };
      }
      return {
        name: item.name,
        id: item.id,
      };
    });

    onSave(
      processedItems,
      editedCollectionName,
      isCollectionPublic,
      editedStage,
    );
    onClose();
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      handleAddItem(); // Call the function to add the item
    }
  };

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = editedItems.findIndex(
      (item) => item.clientId === String(active.id),
    );
    const newIndex = editedItems.findIndex(
      (item) => item.clientId === String(over.id),
    );
    if (oldIndex < 0 || newIndex < 0 || oldIndex === newIndex) {
      return;
    }

    setEditedItems(arrayMove(editedItems, oldIndex, newIndex));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
      <div
        ref={modalRef}
        className="relative flex max-h-[90vh] w-full max-w-4xl flex-col items-center overflow-y-auto rounded-lg p-4 shadow-lg"
        style={{
          backgroundColor: adjustColorForColorblindness(
            theme.cardBackgroundColor || DEFAULT_CARD_BACKGROUND,
          ),
          color: contrastingTextColor(
            theme.cardBackgroundColor || DEFAULT_CARD_BACKGROUND,
          ),
        }}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute right-2 top-2 rounded-lg bg-red-500 px-3 py-2 text-sm text-white transition duration-300 hover:bg-red-600"
        >
          X
        </button>
        <h1 className="mb-4 text-xl font-bold sm:mb-6 sm:text-2xl">
          Edit Collection
        </h1>
        <div className="mb-4 flex flex-col items-center justify-center">
          <label htmlFor="collectionName" className="mb-2 block">
            Collection Name
          </label>
          <input
            type="text"
            id="collectionName"
            className="w-full rounded border border-black bg-white p-2 text-center text-black"
            value={editedCollectionName}
            onChange={(e) => setEditedCollectionName(e.target.value)}
            title="Collection Name"
          />
        </div>
        <div className="mb-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={isCollectionPublic}
              onChange={() => setIsCollectionPublic(!isCollectionPublic)}
              className="mr-2"
            />
            Make Collection Public
          </label>
        </div>
        <div className="mb-4 flex flex-col items-center justify-center">
          <label htmlFor="editStageSelect" className="mb-2 block">
            Collection stage (optional)
          </label>
          <select
            id="editStageSelect"
            className="rounded border border-black bg-white p-2 text-center text-black"
            value={editedStage}
            onChange={(e) =>
              setEditedStage(normalizeCollectionStage(e.target.value))
            }
          >
            {COLLECTION_STAGES.map((value) => (
              <option key={value} value={value}>
                {COLLECTION_STAGE_LABELS[value]}
              </option>
            ))}
          </select>
          <p className="mt-1 max-w-sm text-center text-xs opacity-70">
            Which group this deck belongs to. Does not change when someone
            practices it — retag here if it feels misplaced.
          </p>
        </div>
        <div className="mb-4 flex flex-col items-center justify-center">
          {allowedItemTypes.length === 1 && (
            <div className="mb-2 rounded bg-blue-100 p-2 text-sm text-blue-800">
              This collection can only contain {getItemTypeDisplayName(allowedItemTypes[0])} items.
            </div>
          )}
          <label htmlFor="itemTypeSelect" className="mb-2 block">
            Select Item Type
          </label>
          <select
            id="itemTypeSelect"
            value={selectedItemType}
            onChange={(e) => {
              setSelectedItemType(e.target.value);
              setCustomItemName(""); // Reset custom item name when changing type
              setSelectedTerm(""); // Reset selected term when changing type
            }}
            className="mb-4 rounded border p-2"
          >
            {allowedItemTypes.map(itemType => (
              <option key={itemType} value={itemType}>
                {getItemTypeDisplayName(itemType)}
              </option>
            ))}
          </select>

          {availableTerms.length > 0 && (
            <div className="mb-4 flex flex-col items-center justify-center">
              <label htmlFor="termSelect" className="mb-2 block">
                Select a Term
              </label>
              <select
                id="termSelect"
                value={selectedTerm}
                onChange={(e) => setSelectedTerm(e.target.value)}
                className="mb-4 rounded border p-2"
              >
                <option value="">Select a term</option>
                {availableTerms.map((term, index) => (
                  <option key={index} value={term}>
                    {term}
                  </option>
                ))}
              </select>
            </div>
          )}

          {selectedItemType === "custom" && (
            <div className="mb-4 flex flex-col items-center justify-center">
              <label htmlFor="customItemInput" className="mb-2 block">
                Custom Item Name
              </label>
              <input
                type="text"
                id="customItemInput"
                value={customItemName}
                onChange={(e) => setCustomItemName(e.target.value)}
                onKeyDown={handleKeyDown}
                className="w-full rounded border p-2 text-center"
                placeholder="Enter custom item name"
              />
            </div>
          )}

          {selectedItemType === "numberSense" && (
            <div className="mb-4 flex flex-col items-center justify-center">
              <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="dotColor" className="mb-2 block">
                    Dot Color
                  </label>
                  <select
                    id="dotColor"
                    value={dotColor}
                    onChange={(e) => setDotColor(e.target.value)}
                    className="w-full rounded border p-2"
                  >
                    <option value="blue">Blue</option>
                    <option value="red">Red</option>
                    <option value="green">Green</option>
                    <option value="black">Black</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="dotShape" className="mb-2 block">
                    Dot Shape
                  </label>
                  <select
                    id="dotShape"
                    value={dotShape}
                    onChange={(e) => setDotShape(e.target.value)}
                    className="w-full rounded border p-2"
                  >
                    <option value="circle">Circle</option>
                    <option value="square">Square</option>
                    <option value="triangle">Triangle</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          <button
            type="button"
            className="flex size-10 items-center justify-center rounded-full border border-black bg-green-500 text-2xl text-white transition duration-300 hover:bg-green-600"
            onClick={handleAddItem}
          >
            +
          </button>
        </div>
        <div className="mt-6 w-full">
          <DndContext
            sensors={dndSensors}
            collisionDetection={gridCollisionDetection}
            onDragEnd={onDragEnd}
          >
            <SortableContext
              items={editedItems.map((item) => item.clientId)}
              strategy={rectSortingStrategy}
            >
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3">
                {editedItems.map((item, index) => {
                  const previewSrc = getItemPreviewSrc(item);
                  const canEditCountingSvg = isCountingSvg(item.svg);
                  const showNameInput =
                    !previewSrc && !item.svg && !isImageSrc(item.name);

                  return (
                    <SortableGridItem
                      key={item.clientId}
                      id={item.clientId}
                      className="relative cursor-grab rounded-md border border-black bg-white p-2 text-black active:cursor-grabbing"
                    >
                      <button
                        type="button"
                        aria-label="Remove item"
                        onPointerDown={(e) => e.stopPropagation()}
                        onClick={() => handleRemoveItem(index)}
                        className="absolute right-1 top-1 z-10 rounded-lg bg-red-500 px-3 py-2 text-sm text-white transition duration-300 hover:bg-red-600"
                      >
                        X
                      </button>
                      <div className="flex min-w-0 flex-col items-center pt-8">
                        {previewSrc && canPairUploadedImage(previewSrc) ? (
                          <PairSlideImages
                            primarySrc={item.svgs?.[0] || previewSrc}
                            secondarySrc={item.svgs?.[1]}
                            alt={
                              isImageSrc(item.name)
                                ? "Collection item"
                                : item.name || "Collection item"
                            }
                            imageClassName="mb-2 max-h-40 w-full object-contain"
                            onSecondaryChange={(src) => {
                              const newItems = [...editedItems];
                              newItems[index] = {
                                ...item,
                                ...slideImageFields(
                                  item.svgs?.[0] || previewSrc,
                                  src,
                                ),
                              };
                              setEditedItems(newItems);
                            }}
                          />
                        ) : previewSrc ? (
                          <ColorblindImage
                            src={previewSrc}
                            alt={
                              isImageSrc(item.name)
                                ? "Collection item"
                                : item.name || "Collection item"
                            }
                            className="mb-2 max-h-40 w-full object-contain"
                          />
                        ) : null}
                        {!previewSrc && item.svg && (
                          <ColorblindImageContainer className="mb-2 h-auto w-full" style={{ minHeight: "100px" }}>
                            <div
                              dangerouslySetInnerHTML={{
                                __html: decodeSvgData(item.svg) || "",
                              }}
                            />
                          </ColorblindImageContainer>
                        )}
                        {canEditCountingSvg && (
                          <div className="mt-2 w-full">
                            <input
                              title="Count"
                              placeholder="Enter count"
                              type="number"
                              value={item.count || 1}
                              onPointerDown={(e) => e.stopPropagation()}
                              onChange={(e) => {
                                const newItems = [...editedItems];
                                const newCount =
                                  parseInt(e.target.value) || 1;
                                const newSvg = generateCountingSvg(
                                  newCount,
                                  dotColor,
                                  dotShape,
                                );
                                newItems[index] = {
                                  ...item,
                                  name: `Number Sense Image (Count: ${newCount})`,
                                  count: newCount,
                                  svg: newSvg,
                                };
                                setEditedItems(newItems);
                              }}
                              min="1"
                              className="w-full rounded border border-gray-300 px-2 py-1 text-center"
                            />
                            <div className="mt-2 flex justify-between">
                              <button
                                type="button"
                                onPointerDown={(e) => e.stopPropagation()}
                                onClick={() => {
                                  const newItems = [...editedItems];
                                  const newSvg = generateCountingSvg(
                                    item.count || 1,
                                    dotColor,
                                    dotShape,
                                  );
                                  newItems[index] = {
                                    ...item,
                                    svg: newSvg,
                                  };
                                  setEditedItems(newItems);
                                }}
                                className="rounded bg-blue-500 px-2 py-1 text-white"
                              >
                                Update Style
                              </button>
                            </div>
                          </div>
                        )}
                        {category === PICTURE_QUIZ_CATEGORY && previewSrc && (
                          <div className="mt-2 w-full space-y-2">
                            <div>
                              <label className="block text-left text-xs font-bold">
                                Question
                              </label>
                              <input
                                type="text"
                                value={item.name}
                                onPointerDown={(e) => e.stopPropagation()}
                                onChange={(e) =>
                                  handleEditItemName(index, e.target.value)
                                }
                                className="w-full rounded border border-gray-300 px-2 py-1 text-center"
                              />
                            </div>
                            <div>
                              <label className="block text-left text-xs font-bold">
                                Answer <span className="text-red-500">*</span>
                              </label>
                              <input
                                type="text"
                                value={item.answer || ""}
                                onPointerDown={(e) => e.stopPropagation()}
                                onChange={(e) => {
                                  const newItems = [...editedItems];
                                  newItems[index] = {
                                    ...item,
                                    answer: e.target.value,
                                    answerError: false,
                                  };
                                  setEditedItems(newItems);
                                }}
                                className={`w-full rounded border px-2 py-1 text-center ${
                                  (pictureQuizSaveAttempted ||
                                    item.answerError) &&
                                  !item.answer?.trim()
                                    ? "border-red-500 bg-red-50"
                                    : "border-gray-300"
                                }`}
                              />
                            </div>
                          </div>
                        )}
                        {category === PICTURE_WORDS_CATEGORY && previewSrc && (
                          <div className="mt-2 w-full">
                            <label className="block text-left text-xs font-bold">
                              Word <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              value={item.name}
                              onPointerDown={(e) => e.stopPropagation()}
                              onChange={(e) =>
                                handleEditItemName(index, e.target.value)
                              }
                              className={`w-full rounded border px-2 py-1 text-center ${
                                pictureWordsSaveAttempted &&
                                !item.name?.trim()
                                  ? "border-red-500 bg-red-50"
                                  : "border-gray-300"
                              }`}
                            />
                          </div>
                        )}
                        {showNameInput && (
                          <input
                            type="text"
                            aria-label="Item name"
                            value={item.name}
                            onPointerDown={(e) => e.stopPropagation()}
                            onChange={(e) =>
                              handleEditItemName(index, e.target.value)
                            }
                            className="w-full rounded border border-gray-300 px-2 py-1 text-center"
                          />
                        )}
                      </div>
                    </SortableGridItem>
                  );
                })}
              </div>
            </SortableContext>
          </DndContext>
        </div>
        <div className="mt-6 flex w-full justify-around">
          <button
            type="button"
            className="rounded border border-black bg-green-500 px-4 py-2 text-white transition duration-300 hover:bg-green-600"
            onClick={handleSave}
          >
            Save Collection
          </button>
          <button
            type="button"
            className="rounded border border-black bg-red-500 px-4 py-2 text-white transition duration-300 hover:bg-red-600"
            onClick={onClose}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditCollectionModal;
